import { Hand, Loader2, UserRound } from "lucide-react";

import { MemberTreePicker } from "@/entities/org-department/ui/MemberTreePicker";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";

import type { AcqOpportunityCard } from "../../api/acqService";
import { TERMINAL_STATES } from "../../model/acqLabels";

/**
 * 一条线索归谁跟(契约 §13.1 三动作:管理员分派 / 坐席认领 / 只看我的)。
 *
 * ★★ **两种角色看到的是两个不同的控件,不是同一个控件加个禁用态**:
 *   管理员看到「分派给…」的人名下拉;坐席看到一个「认领」按钮。
 *   给坐席摆一个灰着的下拉,等于每天提醒他一次"你没这个权限" —— 而他要做的事(把这条
 *   揽下来)本来就有自己的按钮。
 * ★ 前端按角色分支只是**不摆一个必然 403 的入口**;门在服务端(契约 §13.4)。
 * ★ 终态(成了 / 没成)不再能改分派:那条线索已经结案,再派给谁都只是把一条死单
 *   挂到一个人名下,还会让「只看我的」里多出一条他什么也做不了的记录。
 *
 * ★★ 0917 批次四(契约 §15.3):选人换成**系统那一个** `MemberTreePicker`(军师记忆审计页
 *   用的同一个)。这里原来是一个自己拉 `/tenant/users` 拼出来的 DropdownMenu —— 同一件事
 *   在获客舱、审计页、派单弹窗上是三种长相,而且这一份还看不见部门。
 *   ★ 打开 `includeUnassigned`:本地真库 3 个在职员工只有 1 个在部门里,只按部门树选人的话
 *     另外两个永远选不到,而后端判「能不能被派」用的是**活跃租户账号**不是部门归属。
 */
export function AssignControl({
  card,
  canAssign,
  meId,
  busy,
  onAssign,
  onClaim,
  className,
}: {
  card: Pick<AcqOpportunityCard, "id" | "state" | "assigneeId" | "assigneeName">;
  /** TENANT_ADMIN / TENANT_MANAGER(与后端同口径)。 */
  canAssign: boolean;
  /** 当前登录的人。用来把"分派给我"的那条显示成「我的」。 */
  meId: number | null;
  busy?: boolean;
  /** `displayName` 只用来把面上的名字当场换掉(乐观回显),不进请求体。 */
  onAssign: (id: string, assigneeId: number | null, displayName?: string) => void;
  onClaim: (id: string) => void;
  className?: string;
}) {
  const assigned = card.assigneeId != null;
  const isMine = assigned && meId != null && card.assigneeId === meId;
  const terminal = TERMINAL_STATES.includes(card.state);
  // ★ 有 id 没名字时写「已分派」,**不拿 id 当名字显示** —— 「分派给 9002」对谁都不是信息。
  const who = card.assigneeName?.trim() || (assigned ? "已分派" : "");

  if (terminal) {
    return (
      <span className={cn("text-2xs text-muted-foreground", className)}>
        {assigned ? `${isMine ? "我" : who}跟的，已结案` : "已结案"}
      </span>
    );
  }

  if (canAssign) {
    return (
      <MemberTreePicker
        value={assigned ? { principalId: card.assigneeId!, displayName: who } : null}
        onChange={(m) => onAssign(card.id, m.principalId, m.displayName)}
        includeUnassigned
        align="end"
        disabled={busy}
        triggerVariant={assigned ? "ghost" : "outline"}
        triggerClassName={cn("h-8 gap-1 px-2 text-2xs", className)}
        triggerLabel={busy ? "分派中…" : assigned ? (isMine ? "我的" : `分派给 ${who}`) : "分派给…"}
        actions={
          assigned
            ? [
                {
                  key: "__unassign__",
                  label: "取消分派（回到公共池）",
                  onSelect: () => onAssign(card.id, null),
                },
              ]
            : []
        }
      />
    );
  }

  // ── 坐席 ────────────────────────────────────────────────────────────────
  if (isMine) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-semibold text-primary",
          className
        )}
      >
        <UserRound className="h-3 w-3" />
        我的
      </span>
    );
  }
  if (assigned) {
    // ★ 已经是别人的:不给按钮。给了也只会 409,而"点了没用"比"没有这个按钮"更让人困惑。
    return <span className={cn("text-2xs text-muted-foreground", className)}>分派给 {who}</span>;
  }
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={() => onClaim(card.id)}
      className={cn("h-8 gap-1 px-2 text-2xs", className)}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hand className="h-3.5 w-3.5" />}
      认领
    </Button>
  );
}
